import { useForm, FormProvider } from "react-hook-form";
import { Button } from "../../../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { FormControl, FormField, FormItem } from "@/src/components/ui/form";
import { useEffect } from "react";
import { useToast } from "@/src/components/ui/use-toast";
import { Textarea } from "@/src/components/ui/textarea";
import { EditBookingBoardingInformationsForm } from "./bookingBoardingInformations.schema";
import { useBookingBoardingInformationsMutation } from "@/src/lib/bookingBoardingInformationsMutation";

type EditBookingBoardingInformationsProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  initialBoardingInformations: string | undefined;
  bookingId: string;
};

export const EditBookingBoardingInformationsModal = ({
  open,
  setOpen,
  initialBoardingInformations,
  bookingId,
}: EditBookingBoardingInformationsProps) => {
  const updateBookingBoardingInformations = useBookingBoardingInformationsMutation(bookingId);
  const { toast } = useToast();

  const form = useForm<EditBookingBoardingInformationsForm>({
    defaultValues: {
      boardingInformations: initialBoardingInformations || "",
    },
  });

  const { handleSubmit, control, reset } = form;

  useEffect(() => {
    reset({ boardingInformations: initialBoardingInformations || "" });
  }, [initialBoardingInformations, reset]);

  const onSubmit = (data: EditBookingBoardingInformationsForm) => {
    const toastId = toast({
      description: "Updating boarding informations...",
      variant: "default",
      duration: Infinity,
    }).id;

    updateBookingBoardingInformations.mutate(
      { boardingInformations: data.boardingInformations, toastId },
      {
        onSuccess: () => {
          setOpen(false);
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(val) => {
        if (!val) {
          setOpen(false);
        }
      }}
    >
      <DialogContent className="max-w-[640px] max-h-[calc(100%-60px)] overflow-y-auto">
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle className="text-3xl font-semi-bold">Edit boarding informations</DialogTitle>
            </DialogHeader>

            <div className="mt-4 space-y-4">
              <FormField
                control={control}
                name={`boardingInformations`}
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2">
                    <FormControl>
                      <Textarea className="min-h-[200px]" {...field} placeholder={`Write boarding informations..`} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter className="flex mt-8 w-full !justify-between">
              <Button type="button" onClick={() => setOpen(false)} variant="outline">
                Cancel
              </Button>
              <Button type="submit" variant="default">
                Save
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};
